/**
 * ClaimCreatePage (CC-Claims) — the split-out create surface.
 *
 * House form contract: react-hook-form + zodResolver (`mode: 'onTouched'`,
 * `reValidateMode: 'onChange'`), the `common/form/*` controlled kit, 44px
 * controls, gray section boxes. The `side` toggle drives the 銷售索賠 / 採購索賠
 * dual placement — it relabels the required order/line link to the SO or PO
 * line the claim must reference (the handler rejects a mismatched orderType).
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, App, Button, Card, Col, Input, InputNumber, Row, Typography, theme } from 'antd';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/components/common/PageHeader';
import ControlledInput from '@/components/common/form/ControlledInput';
import ControlledSelect from '@/components/common/form/ControlledSelect';
import { FormFieldWrapper } from '@/components/common/form/FormFieldWrapper';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import { CLAIM_SIDES, CLAIM_TYPES } from '@/types/api/claim';
import type { ClaimOrderType, ClaimSide, ClaimType, CreateClaimRequest } from '@/types/api/claim';
import { useClaimMutations } from './useClaims';
import { extractClaimApiError } from './claimConstants';

const { Text } = Typography;

interface ClaimCreateFormValues {
  side: ClaimSide;
  type: ClaimType;
  orderNumber: string;
  orderId: string;
  lineId: string;
  productId: string;
  productName: string;
  quantity?: number;
  amount?: number;
  currency: string;
  evidenceNote: string;
  owner: string;
  actor: string;
}

const ClaimCreatePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['claims', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();
  const { message } = App.useApp();
  const formStyles = useFormStyles();
  const { createClaim } = useClaimMutations();

  const schema = useMemo(
    () =>
      z
        .object({
          side: z.enum(['sales', 'purchasing'], {
            errorMap: () => ({ message: t('claims:validation.side') }),
          }),
          type: z.enum(['shortage', 'damage', 'quality', 'price', 'other'], {
            errorMap: () => ({ message: t('claims:validation.type') }),
          }),
          orderNumber: z.string().trim().min(1, t('claims:validation.required')),
          orderId: z.string().trim().min(1, t('claims:validation.required')),
          lineId: z.string().trim().min(1, t('claims:validation.required')),
          productId: z.string().trim().min(1, t('claims:validation.required')),
          productName: z.string().trim().min(1, t('claims:validation.required')),
          quantity: z.number().positive(t('claims:validation.quantityPositive')).optional(),
          amount: z.number().positive(t('claims:validation.amountPositive')).optional(),
          currency: z.string().trim().length(3, t('claims:validation.currency')),
          evidenceNote: z.string().trim().min(10, t('claims:validation.evidenceMin')),
          owner: z.string().trim().min(1, t('claims:validation.required')),
          actor: z.string().trim().min(1, t('claims:validation.required')),
        })
        .refine((data) => (data.quantity ?? 0) > 0 || (data.amount ?? 0) > 0, {
          message: t('claims:validation.impact'),
          path: ['quantity'],
        }),
    [t]
  );

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ClaimCreateFormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      side: 'sales',
      type: undefined,
      orderNumber: '',
      orderId: '',
      lineId: '',
      productId: '',
      productName: '',
      quantity: undefined,
      amount: undefined,
      currency: 'TWD',
      evidenceNote: '',
      owner: '',
      actor: '',
    },
  });

  const side = useWatch({ control, name: 'side' });
  const isPurchasing = side === 'purchasing';

  const sectionBoxStyle: React.CSSProperties = {
    backgroundColor: token.colorFillQuaternary,
    borderRadius: radiusTokens.panel,
    padding: 20,
    marginBottom: token.marginLG,
  };
  const sectionTitleStyle: React.CSSProperties = {
    display: 'block',
    fontSize: token.fontSize,
    fontWeight: token.fontWeightStrong,
    marginBottom: token.marginMD,
  };

  const onSubmit = async (values: ClaimCreateFormValues) => {
    const orderType: ClaimOrderType = values.side === 'sales' ? 'sales_order' : 'purchase_order';
    const body: CreateClaimRequest = {
      side: values.side,
      type: values.type,
      reference: {
        orderType,
        orderId: values.orderId.trim(),
        orderNumber: values.orderNumber.trim(),
        lineId: values.lineId.trim(),
        productId: values.productId.trim(),
        productName: values.productName.trim(),
      },
      quantity: values.quantity,
      amount: values.amount,
      currency: values.currency.trim().toUpperCase(),
      evidenceNote: values.evidenceNote.trim(),
      owner: values.owner.trim(),
      actor: values.actor.trim(),
    };

    try {
      const created = await createClaim(body);
      message.success(t('claims:create.success', { claimNumber: created.claimNumber }));
      navigate(`/auth/claims/${created.id}`);
    } catch (error) {
      const { message: detail } = extractClaimApiError(error);
      message.error(detail || t('claims:messages.createFailed'));
    }
  };

  return (
    <div className="tw-px-6 tw-pb-8">
      <PageHeader title={t('claims:create.title')} />

      <Card
        style={{
          maxWidth: 960,
          ...elevation.card,
        }}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: token.marginLG }}
          message={isPurchasing ? t('claims:create.purchasingHelp') : t('claims:create.salesHelp')}
        />

        {/* Classification */}
        <div style={sectionBoxStyle}>
          <Text style={sectionTitleStyle}>{t('claims:create.sections.classification')}</Text>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <ControlledSelect<ClaimCreateFormValues>
                name="side"
                control={control}
                label={t('claims:fields.side')}
                required
                options={CLAIM_SIDES.map((value) => ({ value, label: t(`claims:sides.${value}`) }))}
              />
            </Col>
            <Col xs={24} md={12}>
              <ControlledSelect<ClaimCreateFormValues>
                name="type"
                control={control}
                label={t('claims:fields.type')}
                required
                options={CLAIM_TYPES.map((value) => ({ value, label: t(`claims:types.${value}`) }))}
              />
            </Col>
          </Row>
        </div>

        {/* Order line reference (dual placement) */}
        <div style={sectionBoxStyle}>
          <Text style={sectionTitleStyle}>{t('claims:create.sections.reference')}</Text>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <ControlledInput<ClaimCreateFormValues>
                name="orderNumber"
                control={control}
                label={isPurchasing ? t('claims:fields.poNumber') : t('claims:fields.soNumber')}
                required
              />
            </Col>
            <Col xs={24} md={12}>
              <ControlledInput<ClaimCreateFormValues>
                name="orderId"
                control={control}
                label={t('claims:fields.orderId')}
                required
              />
            </Col>
            <Col xs={24} md={12}>
              <ControlledInput<ClaimCreateFormValues>
                name="lineId"
                control={control}
                label={t('claims:fields.lineId')}
                required
              />
            </Col>
            <Col xs={24} md={12}>
              <ControlledInput<ClaimCreateFormValues>
                name="productId"
                control={control}
                label={t('claims:fields.productId')}
                required
              />
            </Col>
            <Col xs={24}>
              <ControlledInput<ClaimCreateFormValues>
                name="productName"
                control={control}
                label={t('claims:fields.product')}
                required
              />
            </Col>
          </Row>
        </div>

        {/* Impact */}
        <div style={sectionBoxStyle}>
          <Text style={sectionTitleStyle}>{t('claims:create.sections.impact')}</Text>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Controller
                name="quantity"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormFieldWrapper label={t('claims:fields.quantity')} error={error?.message}>
                    <InputNumber
                      value={field.value ?? undefined}
                      onChange={(value) => field.onChange(value ?? undefined)}
                      onBlur={field.onBlur}
                      min={0.01}
                      size="large"
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={error ? 'error' : undefined}
                    />
                  </FormFieldWrapper>
                )}
              />
            </Col>
            <Col xs={24} md={8}>
              <Controller
                name="amount"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <FormFieldWrapper label={t('claims:fields.amount')} error={error?.message}>
                    <InputNumber
                      value={field.value ?? undefined}
                      onChange={(value) => field.onChange(value ?? undefined)}
                      onBlur={field.onBlur}
                      min={0.01}
                      precision={2}
                      size="large"
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={error ? 'error' : undefined}
                    />
                  </FormFieldWrapper>
                )}
              />
            </Col>
            <Col xs={24} md={8}>
              <ControlledInput<ClaimCreateFormValues>
                name="currency"
                control={control}
                label={t('claims:fields.currency')}
                required
                maxLength={3}
              />
            </Col>
          </Row>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t('claims:create.impactHelp')}
          </Text>
        </div>

        {/* Evidence & ownership */}
        <div style={sectionBoxStyle}>
          <Text style={sectionTitleStyle}>{t('claims:create.sections.evidence')}</Text>
          <Controller
            name="evidenceNote"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormFieldWrapper
                label={t('claims:fields.evidence')}
                required
                error={error?.message}
              >
                <Input.TextArea
                  {...field}
                  rows={3}
                  showCount
                  maxLength={1000}
                  status={error ? 'error' : undefined}
                />
              </FormFieldWrapper>
            )}
          />
          <Row gutter={[16, 16]} style={{ marginTop: token.marginMD }}>
            <Col xs={24} md={12}>
              <ControlledInput<ClaimCreateFormValues>
                name="owner"
                control={control}
                label={t('claims:fields.owner')}
                required
              />
            </Col>
            <Col xs={24} md={12}>
              <ControlledInput<ClaimCreateFormValues>
                name="actor"
                control={control}
                label={t('claims:fields.createdBy')}
                required
              />
            </Col>
          </Row>
        </div>

        {/* Footer actions */}
        <div className="tw-flex tw-justify-end tw-gap-3">
          <Button
            size="large"
            onClick={() => navigate('/auth/claims')}
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="primary"
            size="large"
            loading={isSubmitting}
            onClick={handleSubmit(onSubmit)}
            style={{
              height: `${controlHeightTokens.controlHeightXL}px`,
              paddingLeft: token.paddingLG,
              paddingRight: token.paddingLG,
            }}
          >
            {t('claims:create.submit')}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default ClaimCreatePage;
